import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { theme } from '../theme'
import { fmtDate } from '../utils/datetime'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'

/**
 * Settings → Provider Accounts. ONE component for EVERY account kind (T5.40).
 *
 * NO PER-PROVIDER JSX, AND THAT IS THE POINT. Every field this form renders — its key, its
 * label, whether it is a secret, whether it is required, and the `has_*` flag that proves it is
 * stored — comes from `GET /api/settings/provider-accounts/kinds`, which the backend derives
 * from the provider registry's own `LegCredentialSpec`. A `{kind === 'sfax' && ...}` branch here
 * would be a second declaration of the registry, and the two would drift the moment a provider
 * added a field: the symptom is a form that silently drops a value the backend requires, which
 * is exactly the defect that once made SFax unselectable through the job editor.
 *
 * THE CALLBACK URLS ARE SERVER-GENERATED (T5.42) and never assembled here. The URL is typed by
 * hand into a provider's portal; a browser building it from `window.location` would be right in
 * every environment the developer tested and silently wrong behind a proxy or on a stage, and
 * the symptom is a provider delivering to an address nothing serves — indistinguishable from a
 * quiet period. Three states are rendered distinctly, because they need different actions: the
 * kind has no account-level callbacks (nothing to register), the kind has them but PUBLIC_FQDN
 * is unconfigured (fix the deployment), and the URLs are present (paste them into the portal).
 *
 * A GREEN TICK MEANS ONE THING (T5.38). The test button reports the `check` the server ran —
 * `token`, `smtp_auth`, `imap_select`, `completeness`, `decrypt` — rather than a bare success,
 * because four kinds need four different pieces of evidence and they are not interchangeable.
 * `check: "unsupported"` is rendered as NOTHING PROVED: neither a credential failure nor a
 * pass. A button that reported success because it did nothing would be worse than absent.
 *
 * SECRETS ARE WRITE-ONLY. The API returns `presence` booleans and never a value; leaving a
 * credential box empty KEEPS what is stored, which is what stops an operator saving an
 * unrelated change and silently destroying a working credential.
 *
 * Server-side gating is the real control (`require_admin` plus the `/api/settings/` middleware
 * prefix). The `useIsAdmin` check here only decides what to render.
 */

const API = '/api/settings/provider-accounts'
// U4 — the mailcow settings router, already mounted and already admin-gated by its prefix.
const MAILCOW_API = '/api/settings/mailcow'

/**
 * U29 — WHY THE MAILBOX CONTROLS ARE NOT OFFERED, said out loud, in every state where they are not.
 *
 * THE DEFECT THIS REPLACES. The whole provisioning panel was gated on `mailcow?.available`, so
 * every unavailable state rendered ABSOLUTELY NOTHING: an operator whose deployment had mailcow
 * fully configured, with a key that decrypts, read a form with no trace of the feature and
 * reported it "completely missing from the UI still". Nothing distinguished "switched off" from
 * "did not answer" from "never built", because all three render the same empty space. That is this
 * codebase's signature failure — a surface misdescribing a working engine — and an empty region is
 * its worst form, because there is not even a wrong sentence to correct.
 *
 * KEYED BY THE SERVER'S OWN STATE STRING, plus two keys only the browser can be in. The mailboxes
 * endpoint answers HTTP 200 in every failure with `state` and a `reason`, so the state is the
 * server's word and the sentence is this page's; `tests/test_u4_mailcow_unavailable_is_explained.py`
 * asserts every `mailcow.STATUS_*` constant the service module defines has a key here, so a new
 * state added to the service fails loudly instead of falling into a silent default.
 *
 * `configured` IS THE UNREACHABLE CASE and is the operator's. `GET /api/settings/mailcow` reports
 * configuration and performs no I/O — "Configured (reachability not asserted)" is precisely that
 * claim — while the mailboxes call is the first thing in the application that reaches the host. So
 * a state of `configured` arriving with `available: false` means the configuration is right and the
 * host did not answer it, which is a network or an allow_from question and not a settings one.
 *
 * EACH SENTENCE MUST IMPLY A DIFFERENT ACTION. Switching provisioning on, re-encrypting a key
 * under this deployment's SECRET_KEY, and opening an egress path are three unrelated pieces of
 * work; copy that blurs them sends the operator to the wrong one.
 */
const MAILCOW_VERDICTS: Record<string, { headline: string; body: string }> = {
  loading: {
    headline: 'Checking whether a mail server can supply this mailbox…',
    body:
      'This is a live call to the configured mailcow host, so it can take a few seconds. Whatever ' +
      'it answers, the connection settings below are already typeable.',
  },
  status_unreadable: {
    headline: 'This page could not read the mailcow status.',
    body:
      'The request to /api/settings/mailcow/mailboxes did not return a usable answer, so this is a ' +
      'fault in this page\u2019s own call rather than a verdict about mailcow: that route is ' +
      'admin-only, and a session without the admin role is answered but not served. Reload as an ' +
      'admin to see the real state.',
  },
  disabled: {
    headline: 'Mailbox provisioning is switched off for this deployment.',
    body:
      'Nothing is broken and nothing failed: MAILCOW_PROVISIONING_ENABLED is not true, so this ' +
      'deployment never contacts a mail server and no create-or-select control can be offered. ' +
      'Set it to true, redeploy, and this panel will offer to create the mailbox for you.',
  },
  not_configured: {
    headline: 'Provisioning is switched on, but its settings are incomplete.',
    body:
      'The switch is on and at least one required setting is absent, so there is no host to ask. ' +
      'The missing settings are named below \u2014 supply them in env.sh, redeploy, and this panel ' +
      'becomes a create-or-select control.',
  },
  key_unreadable: {
    headline: 'The stored mailcow API key does not decrypt on this deployment.',
    body:
      'A key IS stored and this process cannot read it, which is a different fault from a missing ' +
      'setting and from an unreachable host: no request was attempted, because there is no usable ' +
      'credential to send. MAILCOW_API_KEY_ENC must be ciphertext produced under the SAME ' +
      'SECRET_KEY this deployment runs with \u2014 re-encrypt it and update the SSM parameter. ' +
      'The task definition needs no change.',
  },
  configured: {
    headline: 'mailcow is fully configured here, and did not answer this request.',
    body:
      'Every setting resolves and the stored key decrypts \u2014 which is all the Settings page ' +
      'claims, because reading configuration contacts nothing. This request is the first thing ' +
      'that actually reaches the host, and it failed, so what is missing is the PATH and not the ' +
      'configuration: the service runs in private subnets with no public IP, so it needs NAT ' +
      'egress to the mail host, and mailcow\u2019s allow_from must list this deployment\u2019s ' +
      'egress address or the API key is refused. The exact failure is quoted below.',
  },
  unknown: {
    headline: 'mailcow reported a state this page does not recognise.',
    body:
      'The state is named below exactly as the server sent it. The controls are withheld because ' +
      'this page cannot tell what withholding them would cost, which is a deliberate choice and ' +
      'not a failure of the form.',
  },
}

/**
 * The one sentence every verdict ends with, because it is the operator's standing rule.
 *
 * Rendered from a single constant rather than written into each entry so it cannot drift into
 * saying something weaker in one state than another.
 */
const MAILCOW_MANUAL_UNAFFECTED =
  'This changes nothing below: every connection setting is typed by hand exactly as it is today, ' +
  'and no field on this form is disabled, hidden or required by whether mailcow answers.'

/**
 * WHICH sentence to show. Total by construction — every input maps to a key that exists.
 *
 * Returns `null` in exactly one case: mailcow answered and the controls are offered, so there is
 * nothing to explain. Every other case names a key in {@link MAILCOW_VERDICTS}, which is what makes
 * "no state renders an empty region" a property of this function rather than a hope about the JSX.
 */
function mailcowVerdictKey(
  probe: 'idle' | 'loading' | 'settled',
  status: MailcowMailboxes | null
): string | null {
  if (probe === 'idle' || probe === 'loading') return 'loading'
  if (status === null) return 'status_unreadable'
  if (status.available) return null
  return status.state in MAILCOW_VERDICTS && status.state !== 'loading' ? status.state : 'unknown'
}

/** What ONE credential value can be, per the registry's declared `kind`.
 *
 *  `int` fields (`smtp_port`, `imap_port`) hold numbers and `bool` fields (`smtp_tls_validate`,
 *  `imap_tls_validate`) hold booleans — the server returns and stores them as such. Modelling
 *  every credential as a string is what made the response annotation lie about its own data and
 *  500 the whole account list; the same narrowing here is what forced a flag through a text box.
 */
type FieldValue = string | number | boolean

/** An in-progress create/edit form. Also carries `display_name`, which is always a string. */
type CredentialDraft = Record<string, FieldValue>

interface FieldDescriptor {
  key: string
  label: string
  input: string
  secret: boolean
  /** Whether this field's CURRENT value comes back in `values` on READ. Distinct from `secret`,
   *  which is about storage: a `*_enc` username is encrypted at rest (so it is sent write-only on
   *  save) AND legible to the admin who owns the account (so its value is returned). Keying the
   *  form's masking on `secret` is what rendered every username as "stored — leave blank to keep"
   *  on the only screen that could correct it (U9). */
  displayable: boolean
  required: boolean
  /** A CLOSED set of permitted values, or null for free text. Present for fields the backend
   *  validates against a compiled-in allowlist — the API base URL, whose regional endpoints a
   *  reader cannot otherwise distinguish. */
  choices: string[] | null
  help: string | null
  presence_flag: string
  /** What a BLANK NEW-account draft starts from, or null for no pre-fill — declared in the
   *  backend credential registry beside the field, not here, so the form cannot disagree with the
   *  registry about which port a mailbox uses.
   *
   *  READ ON THE CREATE FORM ONLY. The edit form must never consult it: applying a default to an
   *  account that already stores a value would let opening a record and saving it silently
   *  re-configure the very port the screen was opened to show. Typed as declared — a port is an
   *  `int` on the wire and stays one, which is why this is not `string`. */
  form_default: FieldValue | null
}

/** U4 — one existing mailbox the configured domain already has. */
interface MailcowMailbox {
  address: string
  local_part: string
  quota_mib: number | null
  active: boolean
  name: string
  /**
   * U29/U30 — the provider account that ALREADY holds this mailbox, or null.
   *
   * At most one, because `idx_provider_account_mail_address_unique` is a unique index on
   * `lower(mail_address)` and is global rather than per-kind: a DID may be shared across account
   * kinds, a mailbox may not be held twice. So this agrees with the constraint rather than
   * inventing a second notion of duplication — and it names WHERE, because "taken" without a
   * holder is the half of the answer an operator cannot act on.
   */
  configured_by: { account_id: string; display_name: string; kind: string } | null
}

/**
 * `GET /api/settings/mailcow/mailboxes`.
 *
 * `available` means mailcow ANSWERED — strictly more than "is configured". Every failure arrives
 * here as `available: false` with a `reason` at HTTP 200, never as an error status, so a form that
 * checked `res.ok` would still get a usable answer and a page that failed to load this would still
 * render every manual field.
 */
interface MailcowMailboxes {
  available: boolean
  state: string
  reason: string | null
  domain: string
  mail_host: string
  default_quota_mib: number
  mailboxes: MailcowMailbox[]
  limits: {
    mboxes_used: number
    mboxes_left: number
    max_mailboxes: number
    max_quota_mib: number | null
    default_quota_mib: number | null
  } | null
  password_policy: { min_length: number; requires_special: boolean } | null
  /**
   * U30 — the server's own wording for what a password reset does to everyone else.
   *
   * Taken from the response rather than written here, so this page cannot drift from
   * `mailcow.PASSWORD_RESET_WARNING`. It arrives in EVERY state, including `available: false`,
   * because it states what a reset does rather than anything about this deployment.
   */
  password_reset_warning: string | null
}

/** What a provision attempt achieved. `connection` is keyed by the SERVER's own field keys. */
interface MailcowProvisionResult {
  ok: boolean
  step: string
  /** Which of `create` / `select` / `reset` produced this. `rotated` is only ever true for reset. */
  mode: string
  message: string
  mailbox: string | null
  existed: boolean
  rotated: boolean
  quota_mib: number | null
  connection: Record<string, FieldValue>
  derived_from: string | null
}

interface KindDescriptor {
  kind: string
  label: string
  fields: FieldDescriptor[]
  /** `{transport: that transport's OWN required field keys}` — U4.
   *
   *  WHY THE FORM CANNOT USE `fields[].required` ALONE for a multi-transport kind.
   *  `required` is the UNION over the kind's legs (a field any leg needs is needed of the
   *  record), which is the right answer for every single-transport kind and the wrong one for
   *  `mail`: an outbound SMTP leg and an inbound IMAP leg read the SAME record, and a mailbox
   *  may legitimately receive without sending. The server judges completeness per group and
   *  accepts an IMAP-only mailbox; the form, having only the union, marked all ten inputs
   *  `required` and the browser refused to submit exactly that configuration. A screen that
   *  refuses what the server accepts is indistinguishable, to the operator, from a server that
   *  refuses it.
   *
   *  Empty or single-entry for a single-transport kind, and then every rule below degenerates to
   *  the plain union check `efax_api`, `sfax` and `srfax_api` always used.
   */
  credential_groups: Record<string, string[]>
  /** The keys EVERY group declares — the mailbox address and its one credential set. Required of
   *  the record however the record is used, so these are always `required` in the form. */
  shared_keys: string[]
  has_account_callbacks: boolean
  did_roles: string[]
  /**
   *  B31 — whether accounts of this kind belong to a provider that POLLS for outbound status,
   *  and the bounds the server enforces on the cadence.
   *
   *  ASKED OF THE SERVER, NEVER LISTED HERE. The server discovers the polling kinds from the
   *  provider registry, so a provider added later gets this settings section with no edit to
   *  this file — the failure mode a hand-maintained list produces is a provider that starts
   *  polling and silently never gets its cadence surface.
   *
   *  `polls_outbound_status` false means OMIT the section: a mailbox has no status endpoint, and
   *  a control that stores a value nothing reads is worse than no control. Optional so an older
   *  API response still parses, and absent is treated as "does not poll" rather than assumed.
   */
  polls_outbound_status?: boolean
  /** The PROVIDER's own declared cadence, shown as the placeholder — what clearing the box means. */
  poll_interval_default_seconds?: number | null
  poll_interval_min_seconds?: number | null
  poll_interval_max_seconds?: number | null
  poll_backoff_max_seconds_ceiling?: number | null
}

/** What a transport group is CALLED on screen.
 *
 *  Presentation only — the group KEYS and their membership come from the server, and this map
 *  never decides which fields exist or which are required. An unknown transport degrades to its
 *  own uppercased key rather than being dropped, so a transport added to the registry later
 *  renders correctly with no edit here.
 */
const TRANSPORT_LABELS: Record<string, { title: string; gloss: string }> = {
  smtp: {
    title: 'SMTP submission — sending',
    gloss: 'Fill this in to send through this mailbox. Leave it empty for a receive-only mailbox.',
  },
  imap: {
    title: 'IMAP retrieval — receiving',
    gloss: 'Fill this in to collect inbound mail. Leave it empty for a send-only mailbox.',
  },
}

/** Whether a transport group has been STARTED — any one of its own fields carries a value.
 *
 *  This is the whole of the per-group rule, and it mirrors the server's: a group left entirely
 *  empty is a CHOICE (that protocol is not configured) and requires nothing; a group with one box
 *  filled is a half-finished entry and requires the rest. Anything else either blocks a valid
 *  single-protocol mailbox or lets a half-typed one through to fail at send time.
 */
function groupStarted(keys: string[], draft: CredentialDraft): boolean {
  return keys.some(key => {
    const value = draft[key]
    if (value === undefined || value === null || value === '') return false
    // `false` is a real answer for a bool flag, but an untouched checkbox also reads `false`, so
    // a flag alone never counts as starting a group. Only a host/port/credential does.
    if (value === false) return false
    return true
  })
}

interface JobReference {
  job_id: string
  job_name: string
  legs: string[]
  deleted: boolean
}

interface DeclaredDid {
  id: string
  did: string
  did_normalised: string
  /** The `+`-prefixed E.164 form to SHOW. Served by the API so the `+1`-for-ten-digits
   *  assumption lives in one place; rendering `did` raw is what made one number look like two. */
  did_display: string
  role: string
  did_source: string | null
}

/** THE ROLES A DECLARATION CAN HOLD, when the registry has not answered yet.
 *
 *  The authoritative list is the kinds descriptor's `did_roles`, which the server derives from
 *  `DID_ROLES`. This constant exists only so the form still offers both roles during the first
 *  render, before `GET /kinds` returns — an empty role list would render a declare form that
 *  cannot declare anything, and the operator would read that as the feature being missing.
 */
export const FALLBACK_DID_ROLES = ['inbound', 'outbound_ani']

/** What a role is CALLED on screen, and what choosing it means.
 *
 *  Presentation only, exactly like `TRANSPORT_LABELS`: the roles themselves come from the server
 *  and this map never decides which exist. A role added to the registry later degrades to its own
 *  key rather than being dropped, so it is still offered and still declarable with no edit here.
 *
 *  Roles are PROVIDER-INDEPENDENT. There is deliberately no per-kind branch anywhere in this
 *  block — `inbound` means "faxes arrive at this number" for eFax, SFax and SRFax alike, and a
 *  `kind === 'srfax_api'` special case here would be a second declaration of the registry.
 */
const ROLE_LABELS: Record<string, { title: string; gloss: string }> = {
  inbound: {
    title: 'Receiving (inbound)',
    gloss: 'Faxes sent TO this number are admitted for this account.',
  },
  outbound_ani: {
    title: 'Sending (outbound ANI)',
    gloss: 'This number may be used as the caller ID we send FROM.',
  },
}

/** The human name of a role, or the role key itself when the registry knows one we do not. */
export function roleLabel(role: string): string {
  return ROLE_LABELS[role]?.title ?? role
}

export function roleGloss(role: string): string | null {
  return ROLE_LABELS[role]?.gloss ?? null
}

/** The roles to OFFER: the descriptor's own list, or the fallback until it arrives.
 *
 *  Sorted for a stable order, and de-duplicated because a role offered twice would render two
 *  checkboxes for one declaration — the same one-thing-looks-like-two defect as an ungrouped DID.
 */
export function availableDidRoles(descriptorRoles: string[] | null | undefined): string[] {
  const source = descriptorRoles && descriptorRoles.length > 0 ? descriptorRoles : FALLBACK_DID_ROLES
  return Array.from(new Set(source)).sort()
}

/** THE DEFAULT SELECTION IS EVERY ROLE, and that is the whole point of this control.
 *
 *  A number an operator owns sends AND receives — that is their model of it, and it is also the
 *  supported round trip this app measures. The API defaulted a declaration to `inbound` alone, so
 *  a declared SRFax number sat on screen looking complete while every send was refused with
 *  "declares no outbound DID (sCallerID)". Nothing on the screen contradicted the operator's
 *  model, so nothing told them where to look: the surface described a number that both legs could
 *  use, and the stored row said otherwise.
 *
 *  Defaulting to all roles cannot silently over-declare in the same way, because a declaration is
 *  what PERMITS a leg rather than what routes it: an outbound_ani row the job never names changes
 *  no send, whereas its absence refuses every one.
 */
export function defaultDidRoleSelection(available: string[]): string[] {
  return [...available]
}

/** Toggle one role in a selection, keeping the offered order rather than click order. */
export function toggleDidRole(selected: string[], role: string, available: string[]): string[] {
  const next = new Set(selected)
  if (next.has(role)) next.delete(role)
  else next.add(role)
  return available.filter(r => next.has(r))
}

/** One entry per NUMBER, carrying every role it is declared in.
 *
 *  Grouped on `did_normalised` because that is what makes two rows the same number — the raw
 *  spellings legitimately differ (`7148449145` from the migration, `+17148449145` typed later)
 *  and comparing them would report one DID as two.
 */
export function groupDidsByNumber(dids: DeclaredDid[]) {
  const byNumber = new Map<string, { normalised: string; display: string; entries: DeclaredDid[] }>()
  for (const d of dids) {
    const existing = byNumber.get(d.did_normalised)
    if (existing) {
      existing.entries.push(d)
    } else {
      byNumber.set(d.did_normalised, {
        normalised: d.did_normalised,
        // Prefer the server's canonical form; fall back to the raw value only if an older
        // response omits it, so the chip is never blank.
        display: d.did_display || d.did,
        entries: [d],
      })
    }
  }
  // Stable order: by number, then roles in a fixed order within each group, so the list does not
  // reshuffle between renders.
  return Array.from(byNumber.values())
    .sort((a, b) => a.normalised.localeCompare(b.normalised))
    .map(g => ({ ...g, entries: [...g.entries].sort((x, y) => x.role.localeCompare(y.role)) }))
}

/** The shape every helper below needs: one NUMBER and the declarations it holds. */
export interface DidNumberGroup {
  normalised: string
  display: string
  entries: { id: string; role: string }[]
}

export function rolesHeld(group: DidNumberGroup): string[] {
  return Array.from(new Set(group.entries.map(e => e.role))).sort()
}

/** The offered roles this number is NOT declared in — what the operator can still add.
 *
 *  This is what makes an inbound-only declaration REPAIRABLE IN PLACE. Delete-and-re-declare was
 *  the only route before, and it is not a route the operator has: a number is declared precisely
 *  because a job uses it, and removing the declaration narrows the admission filter for every
 *  delivery in the meantime. An unknown role the registry offers appears here too, so a role added
 *  to the registry later is addable with no edit in this file.
 */
export function missingRoles(group: DidNumberGroup, available: string[]): string[] {
  const held = new Set(rolesHeld(group))
  return available.filter(r => !held.has(r))
}

/** WHAT UNDECLARING THIS ROW ACTUALLY REMOVES — one role, or the number itself.
 *
 *  A number may be serving a leg the operator is not looking at, and the two cases are not
 *  interchangeable: dropping the `inbound` row of a both-roles number stops admitting deliveries
 *  while sends continue, and dropping the last row removes the number from the account entirely.
 *  A single unlabelled × meant the same click did either, decided by data off screen.
 *
 *  Returns null when the id is not in the group, rather than describing a removal that would not
 *  happen — the caller then has nothing to confirm.
 */
export interface UndeclareIntent {
  scope: 'role' | 'number'
  role: string
  /** The roles that SURVIVE this removal. Empty exactly when `scope` is `number`. */
  remaining: string[]
  /** The button's own text. */
  label: string
  /** The full sentence to confirm with, naming the number and what stays. */
  confirm: string
}

export function undeclareIntent(group: DidNumberGroup, didId: string): UndeclareIntent | null {
  const entry = group.entries.find(e => e.id === didId)
  if (!entry) return null
  const remaining = group.entries.filter(e => e.id !== didId).map(e => e.role)
  const remainingSorted = Array.from(new Set(remaining)).sort()
  if (remainingSorted.length === 0) {
    return {
      scope: 'number',
      role: entry.role,
      remaining: [],
      label: 'Undeclare number',
      confirm:
        `Undeclare ${group.display} from this account? It is declared for ` +
        `${roleLabel(entry.role).toLowerCase()} only, so this removes the number entirely.`,
    }
  }
  return {
    scope: 'role',
    role: entry.role,
    remaining: remainingSorted,
    label: `Remove ${roleLabel(entry.role).toLowerCase()}`,
    confirm:
      `Stop using ${group.display} for ${roleLabel(entry.role).toLowerCase()}? ` +
      `The number stays declared for ` +
      `${remainingSorted.map(r => roleLabel(r).toLowerCase()).join(' and ')}.`,
  }
}

/** Which roles the server SAYS it declared, or null when its answer does not say.
 *
 *  Tolerant of three response shapes — a list of declarations, one declaration carrying `roles`,
 *  and one carrying a single `role` — because that is the difference between an API that accepts
 *  several roles per declaration and one that predates it. `null` means NOT STATED, never "none":
 *  an answer we cannot read is not evidence of a role being dropped.
 */
export function declaredRolesFromResponse(body: unknown): string[] | null {
  const rolesOf = (item: unknown): string[] | null => {
    if (!item || typeof item !== 'object') return null
    const rec = item as Record<string, unknown>
    if (Array.isArray(rec.roles)) {
      const roles = rec.roles.filter((r): r is string => typeof r === 'string')
      return roles.length > 0 ? roles : null
    }
    if (typeof rec.role === 'string') return [rec.role]
    return null
  }
  if (Array.isArray(body)) {
    const collected = body.flatMap(item => rolesOf(item) ?? [])
    return collected.length > 0 ? Array.from(new Set(collected)).sort() : null
  }
  const single = rolesOf(body)
  return single ? Array.from(new Set(single)).sort() : null
}

/** THE ROLES ASKED FOR THAT DID NOT LAND — the silent-drop check.
 *
 *  This defect began as a request whose role was ignored and a screen that reported success, so a
 *  form that asks for two roles must not report success on the strength of HTTP 200 alone. Empty
 *  when the response does not state its roles, because an unreadable answer proves nothing.
 */
export function rolesNotDeclared(requested: string[], declared: string[] | null): string[] {
  if (declared === null) return []
  const got = new Set(declared)
  return requested.filter(r => !got.has(r))
}

/** The warning to show when the server declared fewer roles than were chosen, else null. */
export function declarationShortfallMessage(
  display: string,
  requested: string[],
  declared: string[] | null,
): string | null {
  const missing = rolesNotDeclared(requested, declared)
  if (missing.length === 0) return null
  const missingNames = missing.map(r => roleLabel(r).toLowerCase()).join(' and ')
  const landed = (declared ?? []).map(r => roleLabel(r).toLowerCase()).join(' and ')
  return (
    `${display} was declared${landed ? ` for ${landed}` : ''}, but NOT for ${missingNames} — ` +
    `this deployment's API did not accept that role. A leg needing ${missingNames} will still be ` +
    `refused, so treat the number as only partly declared.`
  )
}

/** What the server proved, or refused to claim. `check` names the evidence. */
interface CheckResult {
  ok: boolean
  check: string
  kind: string
  field?: string | null
  detail?: string | null
}

interface ProviderAccount {
  id: string
  account_kind: string
  kind_label: string
  provider: string | null
  display_name: string
  enabled: boolean
  presence: Record<string, boolean>
  /** Current values of every DISPLAYABLE credential field; passwords and keys are never sent and
   *  `presence` remains their only signal. Optional so an older API response still parses.
   *
   *  DISPLAYABLE IS NOT THE SAME AS NON-SECRET (U9). A `*_enc` username is stored encrypted and
   *  still returned here, decrypted, because it is legible to the admin who owns the account —
   *  the server filters on each field's own `display_value` declaration, so this can never carry
   *  a password or a key.
   *
   *  NOT `Record<string, string | null>`: the backend returns the COLUMN's own type, so a port
   *  arrives as `587` and a TLS flag as `true`. The response was annotated string-only on the
   *  server too, which 500'd the entire list on the first account with a port. Kept typed rather
   *  than stringified here for the same reason it is not coerced there — `String(false)` is a
   *  non-empty string, and feeding that to a checkbox renders it CHECKED. */
  values?: Record<string, string | number | boolean | null>
  inbound_callback_url: string | null
  /**
   *  The eFax inbound webhook URL for this ACCOUNT (item 4). The API has returned this since the
   *  webhook moved off the job; the type simply never declared it, so the page could not render it
   *  and told the operator this kind had no account-level callback URL instead.
   *
   *  Optional because only `efax_api` mints a target; every other kind returns null.
   */
  webhook_url?: string | null
  /**
   *  Where a HUMAN signs in to this mailbox. Present only on a `mail` account and only when
   *  mailcow is configured; the server derives it from `MAILCOW_API_BASE` rather than from
   *  `imap_host`, which is a different host entirely.
   */
  webmail_url?: string | null
  outbound_callback_url: string | null
  verified_at: string | null
  verified_detail: CheckResult | null
  dids: DeclaredDid[]
  inbound_watermark_id: string | null
  outbound_watermark_id: string | null
  /**
   *  B31 — the outbound status poll cadence, as FOUR distinct facts plus the setting itself.
   *
   *  `poll_interval_seconds` is what an operator SET, and null means they set nothing.
   *  `effective_poll_interval_seconds` is what the poller will actually use, and
   *  `poll_interval_source` says which layer supplied it — `account`, `provider_default` or
   *  `global_setting`. Rendering only the stored value would show an unconfigured account as
   *  having no cadence at all, which is the one thing that is never true: something is always
   *  pacing the poll, and B31 exists because that number lands inside a measured leg.
   *
   *  All optional so an older API response still parses; a missing effective value renders as
   *  "not reported" rather than as a number this screen invented.
   */
  poll_interval_seconds?: number | null
  poll_backoff_max_seconds?: number | null
  effective_poll_interval_seconds?: number | null
  effective_poll_backoff_max_seconds?: number | null
  poll_interval_source?: string | null
  polls_outbound_status?: boolean
  referenced_by: JobReference[]
  updated_at: string
  updated_by: string | null
}

/** The two B31 draft keys. Not credential fields, so `credentialsFrom` never sees them. */
export const POLL_INTERVAL_KEY = 'poll_interval_seconds'
export const POLL_BACKOFF_KEY = 'poll_backoff_max_seconds'

export interface PollCadencePatch {
  /** The fields to MERGE into the PUT body. A key present with `null` CLEARS the setting. */
  patch: Record<string, number | null>
  /** Labels of boxes holding something that is not a whole number. Save must be refused. */
  invalid: string[]
}

/**
 * Turn the two cadence boxes into a PUT patch (B31).
 *
 * THE THREE STATES ARE DISTINCT, AND THAT IS THE WHOLE OF THIS FUNCTION. A credential form has
 * two states — typed and blank — because a secret cannot be displayed and therefore cannot be
 * deliberately cleared. A cadence is displayed, so it has three:
 *
 *   * NOT TOUCHED → the key is OMITTED, and the server leaves what is stored.
 *   * CLEARED (empty box over a stored value) → the key is sent as `null`, which the server
 *     reads as "revert to the provider's own cadence". Omitting it here instead would make the
 *     setting ONE-WAY: an operator could set 60s and never get back to the default.
 *   * A NUMBER → sent as a number.
 *
 * An empty box over a value that was never set sends nothing: there is no instruction in
 * clearing a box that was already clear, and sending `null` would be a write with no intent
 * behind it.
 *
 * NON-NUMERIC INPUT IS REPORTED, NOT DROPPED. Coercing `"9O"` (letter O) to nothing would
 * silently discard the operator's instruction and leave the old cadence pacing a measurement
 * they believe they changed — the class of silent-surface failure this app keeps shipping. The
 * caller refuses the save and names the box.
 *
 * PURE, so the rule is testable without a browser: the distinction between omitted and
 * explicit-null cannot be observed from a rendered form, only from the request body.
 */
export function pollCadencePatch(
  draft: Record<string, FieldValue>,
  stored: { poll_interval_seconds?: number | null; poll_backoff_max_seconds?: number | null },
): PollCadencePatch {
  const patch: Record<string, number | null> = {}
  const invalid: string[] = []
  const boxes: { key: string; label: string; storedValue: number | null | undefined }[] = [
    { key: POLL_INTERVAL_KEY, label: 'Poll interval', storedValue: stored.poll_interval_seconds },
    { key: POLL_BACKOFF_KEY, label: 'Backoff ceiling', storedValue: stored.poll_backoff_max_seconds },
  ]
  for (const box of boxes) {
    const raw = draft[box.key]
    if (raw === undefined) continue // never touched
    const text = String(raw).trim()
    if (text === '') {
      // Only a value that EXISTS can be cleared.
      if (box.storedValue !== null && box.storedValue !== undefined) patch[box.key] = null
      continue
    }
    const n = Number(text)
    if (!Number.isInteger(n) || n <= 0) {
      invalid.push(box.label)
      continue
    }
    patch[box.key] = n
  }
  return { patch, invalid }
}

/** How the effective cadence was arrived at, in the operator's words rather than the API's. */
export function pollIntervalSourceLabel(source: string | null | undefined): string {
  if (source === 'account') return 'set on this account'
  if (source === 'provider_default') return "this provider's own default"
  if (source === 'global_setting') return 'the global FAX_STATUS_POLL_SECONDS setting'
  return 'an unrecorded source'
}

async function readError(res: Response, fallback: string): Promise<string> {  const body = await res.json().catch(() => null)
  const detail = (body as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail)) {
    return (
      detail
        .map(d => (typeof d === 'string' ? d : (d as { msg?: string })?.msg ?? ''))
        .filter(Boolean)
        .join('; ') || fallback
    )
  }
  return fallback
}

/**
 * One callback URL with a copy affordance (T5.42).
 *
 * `unavailable` is stated with its REASON rather than rendered as an empty box: an operator
 * shown a blank field would try to fill it, and the value is not theirs to choose.
 */
/** One credential input, rendered from the registry's descriptor.
 *
 *  WHY A SHARED COMPONENT AND NOT TWO COPIES. The create form and the edit form each rendered
 *  their own `<input>`, and they drifted: the edit form applied a masked placeholder to EVERY
 *  field rather than only to secrets, so `api_base_url` read as "stored — leave blank to keep".
 *  An operator therefore could not see which eFax regional endpoint an account pointed at, and
 *  after the account migration two accounts pointed at the wrong region — every credential test
 *  returned `401 Bad credentials`, which reads as bad keys rather than a wrong host. One
 *  component means the two forms cannot disagree about how a field is presented.
 *
 *  A CLOSED SET RENDERS AS A SELECT. The API base URL is validated against a compiled-in
 *  allowlist, so free text lets an admin save a host the send path will refuse, and offers no
 *  way to see the permitted ones. The options come from the backend descriptor, never from a
 *  copy of the list held here.
 */
/** One credential input, rendered from the registry's declared `kind` (T5.40).
 *
 *  WHY THIS TAKES A TYPED VALUE. The backend returns each non-secret credential as the type its
 *  column holds — `smtp_port` as `587`, `smtp_tls_validate` as `true` — because the registry
 *  declares them `kind="int"` and `kind="bool"`. Every field used to be forced through a text
 *  box, so a boolean reached it as the string `"true"`/`"false"`: both non-empty, so an unchecked
 *  flag was indistinguishable from a checked one, and an operator editing an account had to type
 *  the word rather than see the state. A field whose type the registry declares must render as
 *  that type or the form misreports what is stored.
 */
function FieldInput({
  field,
  value,
  onChange,
  placeholder,
  required,
}: {
  field: FieldDescriptor
  value: string | number | boolean
  onChange: (value: string | number | boolean) => void
  placeholder?: string
  /** U4 — the EFFECTIVE requirement, which is not always the descriptor's own.
   *
   *  For a multi-transport kind a per-protocol field is required only once its group has been
   *  STARTED, because the record is complete with either protocol alone. Defaults to
   *  `field.required` so every single-transport form is byte-identical to before.
   */
  required?: boolean
}) {
  const isRequired = required ?? field.required
  if (field.input === 'bool') {
    // A REAL checkbox over a real boolean. `checked` is derived by comparing against the string
    // forms too, because an older API response (or a value already round-tripped through a text
    // box before this fix) can still hand us `"true"`.
    const checked = value === true || value === 'true'
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
        />
        <span style={{ color: theme.textMuted }}>{checked ? 'enabled' : 'disabled'}</span>
      </label>
    )
  }
  if (field.input === 'int') {
    return (
      <input
        // `value` may legitimately be a number; React wants a string or number, and an empty
        // string is the only way to render "not set" without printing a 0 nobody stored.
        value={value === '' || value === null || value === undefined ? '' : String(value)}
        onChange={e => {
          const raw = e.target.value
          // Empty stays EMPTY, not 0: on the edit form an empty box means KEEP what is stored,
          // and 0 is a port the operator never chose.
          if (raw === '') {
            onChange('')
            return
          }
          const parsed = Number.parseInt(raw, 10)
          onChange(Number.isNaN(parsed) ? raw : parsed)
        }}
        required={isRequired}
        type="number"
        inputMode="numeric"
        style={inputStyle}
        placeholder={placeholder}
      />
    )
  }
  const text = value === null || value === undefined ? '' : String(value)
  if (field.input === 'select' && field.choices && field.choices.length > 0) {
    return (
      <select
        value={text}
        onChange={e => onChange(e.target.value)}
        required={isRequired}
        style={inputStyle}
      >
        {/* Blank is offered ONLY when the field is optional, and it is a real choice rather than
            an empty state: for api_base_url it means "use the provider's default endpoint", which
            is different from every listed one. */}
        {!field.required && <option value="">— use the provider default —</option>}
        {field.choices.map(c => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    )
  }
  return (
    <input
      value={text}
      onChange={e => onChange(e.target.value)}
      required={isRequired}
      type={field.input === 'password' ? 'password' : 'text'}
      autoComplete={field.secret ? 'new-password' : 'off'}
      style={inputStyle}
      placeholder={placeholder}
    />
  )
}

/** One revealed credential with a copy affordance, modelled on `CallbackUrl` above.
 *
 *  A SEPARATE COMPONENT because `copied` is per-row state: one shared flag would flash "Copied" on
 *  the password when the username was copied, and on a screen whose whole purpose is pasting two
 *  values into another tab, that is the difference between confidence and pasting the wrong one.
 */
function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.75rem', color: theme.textMuted, minWidth: 68 }}>{label}</span>
      <code
        style={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          borderRadius: 3,
          padding: '0.2rem 0.4rem',
          fontSize: '0.75rem',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </code>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(value)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        }}
        style={smallButton}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}


function CallbackUrl({ label, url }: { label: string; url: string | null }) {
  const [copied, setCopied] = useState(false)
  if (!url) {
    return (
      <div style={{ fontSize: '0.8rem', color: theme.textMuted }}>
        {label}: unavailable — PUBLIC_FQDN is not configured on this deployment, so the server
        cannot generate the URL. Nothing here is missing from the account.
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.75rem', color: theme.textMuted, minWidth: 68 }}>{label}</span>
      <code
        style={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          borderRadius: 3,
          padding: '0.2rem 0.4rem',
          fontSize: '0.75rem',
          wordBreak: 'break-all',
        }}
      >
        {url}
      </code>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(url)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        }}
        style={smallButton}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

/**
 * What the test button proved (T5.38).
 *
 * THREE OUTCOMES, NOT TWO. `unsupported` is not a failure and not a pass — the account may be
 * perfectly configured and nothing was checked. Colouring it red would send an operator to
 * re-type a correct credential; colouring it green would make the tick mean "we tried nothing".
 */
function CheckVerdict({ result, verifiedAt }: { result: CheckResult | null; verifiedAt: string | null }) {
  if (!result) {
    return (
      <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>
        Never verified — not the same as broken. Press <em>Test</em> to find out.
      </span>
    )
  }
  const nothingProved = result.check === 'unsupported'
  const colour = nothingProved ? '#ffa726' : result.ok ? '#4caf50' : '#ef5350'
  const headline = nothingProved
    ? 'Nothing was proved'
    : result.ok
      ? `Verified — ${result.check}`
      : `Failed — ${result.check}`
  return (
    <div style={{ fontSize: '0.78rem', lineHeight: 1.6 }}>
      <span style={{ color: colour, fontWeight: 600 }}>
        {nothingProved ? '○' : result.ok ? '●' : '✕'} {headline}
      </span>
      {result.ok && verifiedAt ? (
        <span style={{ color: theme.textMuted }}> · evidence taken {fmtDate(verifiedAt)}</span>
      ) : null}
      {result.field ? (
        <div style={{ color: theme.textMuted }}>
          Field: <strong style={{ color: theme.text }}>{result.field}</strong>
        </div>
      ) : null}
      {result.detail ? (
        <div style={{ color: theme.textMuted, maxWidth: 620 }}>{result.detail}</div>
      ) : null}
    </div>
  )
}

export default function ProviderAccounts() {
  const isAdmin = useIsAdmin()
  const { kind: kindParam } = useParams<{ kind?: string }>()
  const [kinds, setKinds] = useState<KindDescriptor[]>([])
  const [kindsError, setKindsError] = useState<string | null>(null)
  const [accounts, setAccounts] = useState<ProviderAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  // TYPED, not string-only: an `int` field holds a number and a `bool` field a boolean, matching
  // both the registry's declared `kind` and the column the server writes. A string-only draft is
  // what forced a checkbox through a text box and made `"false"` indistinguishable from `"true"`.
  const [form, setForm] = useState<CredentialDraft>({})
  const [editing, setEditing] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<CredentialDraft>({})
  const [didDraft, setDidDraft] = useState<Record<string, string>>({})
  // THE ROLE CHOICE, per account, and ABSENT MEANS THE DEFAULT — every role the registry offers.
  // Held as "not chosen yet" rather than initialised to a list so the default cannot go stale when
  // the descriptor arrives after the first render, and so it re-applies after each declaration
  // instead of inheriting the last narrowed selection.
  const [didRoleDraft, setDidRoleDraft] = useState<Record<string, string[]>>({})
  const [testing, setTesting] = useState<string | null>(null)
  /**
   *  The mailbox sign-in an admin has DELIBERATELY revealed, kept per account id.
   *
   *  Held in component state only, and dropped when the panel is closed or the page leaves — this
   *  is the one secret the UI ever holds, so it does not go into localStorage, a URL, or the
   *  snapshot cache. `revealing` is separate so the button can say what it is doing rather than
   *  appearing dead during the round trip.
   */
  const [revealing, setRevealing] = useState<string | null>(null)
  const [revealed, setRevealed] = useState<
    Record<string, { address: string; password: string }>
  >({})
  const [revealError, setRevealError] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  // U4 — MAILCOW IS AN ADDITIONAL PATH AND NEVER A PRECONDITION. `mailcow` stays null when
  // provisioning is off, unconfigured or unreachable, and NOTHING below reads it to decide
  // whether a field renders, is required, or can be typed into. That is the operator's standing
  // rule expressed as a data dependency rather than as a promise: manual entry cannot break when
  // mailcow is absent, because manual entry never asks.
  const [mailcow, setMailcow] = useState<MailcowMailboxes | null>(null)
  // U29 — "not asked yet" is not "asked and got nothing". Without this the null the fetch leaves
  // behind on failure is indistinguishable from the null it starts with, and the panel would
  // accuse mailcow of not answering before anyone had asked it.
  const [mailcowProbe, setMailcowProbe] = useState<'idle' | 'loading' | 'settled'>('idle')
  const [mailcowDid, setMailcowDid] = useState('')
  const [mailcowPick, setMailcowPick] = useState('')
  // U30 — THE OPERATOR'S CHOICE, and the default is the non-destructive one. `mailcowReset` false
  // means "I have this mailbox's password"; true means "reset it", and the server's warning is
  // rendered above the control that can set it, never after it has been used.
  const [mailcowExistingPassword, setMailcowExistingPassword] = useState('')
  const [mailcowReset, setMailcowReset] = useState(false)
  const [mailcowBusy, setMailcowBusy] = useState(false)
  const [mailcowNote, setMailcowNote] = useState<string | null>(null)
  const [mailcowError, setMailcowError] = useState<string | null>(null)

  const descriptor = kinds.find(k => k.kind === kindParam) ?? null

  /** The roles this deployment offers — the registry's answer, not a copy of it. */
  const didRoles = availableDidRoles(descriptor?.did_roles)

  /** The account's current role selection: what was chosen, or BOTH by default. */
  const didRolesFor = (accountId: string): string[] =>
    didRoleDraft[accountId] ?? defaultDidRoleSelection(didRoles)

  /**
   * The draft a NEW account form starts from: every field the registry declares a `form_default`
   * for, at its declared value and declared TYPE.
   *
   * WHY THIS IS SEEDED INTO STATE rather than rendered as a fallback in the input. A value shown
   * but not held is not submitted — the operator would see `993`, press Save, and store NULL,
   * which is the "surface that misdescribes the engine" failure in miniature. Seeding means the
   * pre-filled value is a real, editable, clearable part of the draft that gets sent.
   *
   * NEW DRAFTS ONLY, and that is the load-bearing part. `editForm` is a separate state that is
   * never seeded from here, so an existing account's stored port cannot be overwritten by a
   * default; blank on the edit form keeps what is stored.
   *
   * No `required` flag is touched. A default answers "what do you start from", never "what must
   * you supply".
   */
  const newAccountDefaults = useMemo((): CredentialDraft => {
    const seed: CredentialDraft = {}
    for (const f of descriptor?.fields ?? []) {
      // `null` and `undefined` both mean NO pre-fill. `false` and `0` do not, so this cannot be a
      // truthiness test — a bool field defaulting to false is a real default.
      if (f.form_default === null || f.form_default === undefined) continue
      seed[f.key] = f.form_default
    }
    return seed
  }, [descriptor])

  useEffect(() => {
    if (!isAdmin) return
    fetch(`${API}/kinds`, { credentials: 'same-origin' })
      .then(async res => {
        if (!res.ok) throw new Error(await readError(res, 'Could not load the account kinds'))
        return res.json() as Promise<{ kinds: KindDescriptor[] }>
      })
      .then(body => setKinds(body.kinds ?? []))
      .catch((e: Error) => setKindsError(e.message))
  }, [isAdmin])

  const load = useCallback(() => {
    if (!kindParam) {
      setAccounts([])
      setLoading(false)
      return
    }
    setLoading(true)
    fetch(`${API}?kind=${encodeURIComponent(kindParam)}`, { credentials: 'same-origin' })
      .then(async res => {
        if (!res.ok) throw new Error(await readError(res, 'Could not load the accounts'))
        return res.json() as Promise<ProviderAccount[]>
      })
      .then(setAccounts)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [kindParam])

  useEffect(() => {
    setError(null)
    setNotice(null)
    setShowAdd(false)
    setEditing(null)
    setForm(newAccountDefaults)
    load()
  }, [load])

  // U4/U29 — ASKED, NEVER DEPENDED ON, AND NOW ALWAYS REPORTED. Nothing below reads the answer to
  // decide whether a field renders, is required or can be typed into, so a failure here cannot
  // break the form. What CHANGED in U29 is that a failure is no longer silent: reporting nothing
  // made "mailcow is off", "mailcow did not answer" and "this feature was never built" render
  // identically, and an operator with a fully configured deployment reasonably read the empty space
  // as the third. Fetched only for a kind with more than one transport group, which is the same
  // structural test the sections use rather than a kind literal.
  const groupCount = Object.keys(descriptor?.credential_groups ?? {}).length
  useEffect(() => {
    if (!isAdmin || groupCount < 2) return
    let live = true
    setMailcowProbe('loading')
    fetch(`${MAILCOW_API}/mailboxes`, { credentials: 'same-origin' })
      .then(res => (res.ok ? (res.json() as Promise<MailcowMailboxes>) : null))
      .then(body => {
        if (!live) return
        setMailcow(body)
        // U29 — SETTLED EITHER WAY. A null body here is a real answer about this page's own call
        // and is reported as one; it is no longer indistinguishable from never having asked.
        setMailcowProbe('settled')
      })
      .catch(() => {
        if (!live) return
        setMailcow(null)
        setMailcowProbe('settled')
      })
    return () => {
      live = false
    }
  }, [isAdmin, groupCount])

  /**
   * Act on `<did>@<domain>` with the intent NAMED, then fill the draft from what came back.
   *
   * U30 — NO LONGER ONE HANDLER FOR BOTH BUTTONS. It used to be, and the comment here said so as
   * a virtue: "the server has one code path: creating a new mailbox and selecting an existing one
   * differ only in the `existed` flag reported back. A selected mailbox has a password nobody
   * knows, so it is rotated to one we do". That is exactly the defect the operator found live —
   * picking the mailbox that already existed rotated its password on the shared mail server and
   * broke every client configured with the old one, from a control labelled "use this mailbox".
   *
   * `mode` is now sent explicitly and decides what the server may write. `select` sends the
   * operator's own password and issues NO write; `reset` requires `confirm_reset`, which this page
   * only sets after having rendered the server's warning above the control.
   *
   * THE FILL IS A MERGE OF WHATEVER KEYS THE SERVER NAMED. This page never writes a credential
   * field key: the field table lives on the server, and a page that restated it would drift from
   * it silently. Values land in ordinary editable inputs, so the operator can correct any of them
   * before saving.
   */
  const handleProvision = async (
    did: string,
    mode: 'create' | 'select' | 'reset',
    opts?: { existingPassword?: string; confirmReset?: boolean }
  ) => {
    const wanted = did.trim()
    if (!wanted) {
      setMailcowError('Enter the DID this mailbox is for.')
      return
    }
    setMailcowBusy(true)
    setMailcowError(null)
    setMailcowNote(null)
    try {
      const res = await fetch(`${API}/mailcow/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          did: wanted,
          display_name: form.display_name ?? null,
          mode,
          existing_password: opts?.existingPassword ?? null,
          confirm_reset: opts?.confirmReset ?? false,
          // U31 — WHICH ACCOUNT IS ASKING. The server refuses a mailbox another account already
          // holds BEFORE it touches mailcow, so an account re-selecting (or re-resetting) the
          // mailbox it already owns has to be distinguishable from a second account claiming it.
          // `null` when creating: a new account holds nothing.
          account_id: editing ?? null,
        }),
      })
      if (!res.ok) throw new Error(await readError(res, 'The provisioning request failed'))
      const body = (await res.json()) as MailcowProvisionResult
      if (!body.ok) {
        // `res.ok` IS NOT THE VERDICT — a refusal arrives at HTTP 200 with the failing step
        // named, exactly as the account test button's does. The form is left untouched and
        // completable by hand.
        setMailcowError(`${body.message} (step: ${body.step})`)
        return
      }
      setForm(v => ({ ...v, ...body.connection }))
      // THE NOTE REPORTS WHETHER THE MAIL SERVER WAS WRITTEN TO, because that is the thing the
      // operator could not previously find out. A select says so explicitly rather than saying
      // nothing about it — silence is what let the rotation go unnoticed.
      const what =
        body.mode === 'select'
          ? `Selected the existing mailbox ${body.mailbox} — its password was NOT changed`
          : body.mode === 'reset'
            ? `Reset the password on the existing mailbox ${body.mailbox} — other clients using the old password must now be updated`
            : `Created ${body.mailbox}`
      setMailcowNote(
        `${what}${body.quota_mib ? `, quota ${body.quota_mib} MiB` : ''}` +
          `. Verified by a real IMAP login. ${body.derived_from ?? ''}`
      )
      if (body.mode !== 'create') setMailcowReset(false)
    } catch (e) {
      setMailcowError((e as Error).message)
    } finally {
      setMailcowBusy(false)
    }
  }

  const credentialsFrom = (values: CredentialDraft): Record<string, FieldValue> => {
    // Only non-empty values are sent. An empty box means KEEP, which is what the server does
    // with a blank field — sending it anyway would rely on the server's handling of a value the
    // UI already knows means nothing, and a future server that trusted the body would clear the
    // credential.
    const out: Record<string, FieldValue> = {}
    for (const f of descriptor?.fields ?? []) {
      const v = values[f.key]
      if (v === undefined || v === '') continue
      // `false` IS a value, not an absent one. A truthiness test drops it, which would make an
      // unchecked TLS-validate box unsavable — the operator could turn validation on and never
      // back off. The checkbox always renders the stored state, so submitting it is not a
      // surprise write.
      out[f.key] = v
    }
    return out
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_kind: kindParam,
          display_name: form.display_name ?? '',
          credentials: credentialsFrom(form),
        }),
      })
      if (!res.ok) {
        setError(await readError(res, 'Could not create the account'))
        return
      }
      setForm(newAccountDefaults)
      setShowAdd(false)
      setNotice('Account created. Press Test to prove the credentials before a job depends on them.')
      load()
    } finally {
      setBusy(false)
    }
  }

  const handleSaveEdit = async (id: string) => {
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      // The STORED cadence, read from the loaded row rather than from the form, because
      // `pollCadencePatch` needs to know whether an empty box is a CLEAR of something or a box
      // that was already empty. Absent (an id no longer in the list) degrades to "nothing
      // stored", so an empty box sends nothing instead of a clear nobody asked for.
      const account = accounts.find(a => a.id === id)
      const payload: Record<string, unknown> = { credentials: credentialsFrom(editForm) }
      if (editForm.display_name) payload.display_name = editForm.display_name
      // B31 — merged rather than assigned, and only for a kind that actually polls. An omitted
      // key keeps what is stored; an explicit `null` reverts to the provider's own cadence.
      const cadence = pollCadencePatch(editForm, {
        poll_interval_seconds: account?.poll_interval_seconds,
        poll_backoff_max_seconds: account?.poll_backoff_max_seconds,
      })
      if (cadence.invalid.length > 0) {
        setError(
          `${cadence.invalid.join(' and ')} must be a whole number of seconds, or empty to use ` +
            'the default. Nothing was saved — the cadence bounds a measurement, so a value that ' +
            'cannot be read is refused rather than ignored.',
        )
        return
      }
      Object.assign(payload, cadence.patch)
      const res = await fetch(`${API}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        setError(await readError(res, 'Could not update the account'))
        return
      }
      setEditing(null)
      setEditForm({})
      load()
    } finally {
      setBusy(false)
    }
  }

  const handleToggle = async (account: ProviderAccount) => {
    setError(null)
    setNotice(null)
    const res = await fetch(`${API}/${account.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !account.enabled }),
    })
    if (!res.ok) setError(await readError(res, 'Could not change the account state'))
    load()
  }

  /**
   * Press the live check (T5.38).
   *
   * A CREDENTIAL FAILURE IS A 200 WITH `ok: false`, so `res.ok` is NOT the verdict — the request
   * succeeded and the provider refused, and reading the HTTP status as the answer would report
   * an operator's wrong password as our outage.
   */
  /**
   *  Reveal this mailbox's sign-in AND open webmail, in that order.
   *
   *  ORDER MATTERS. The credentials are fetched FIRST and the tab opened only once they are in
   *  hand, so a failure leaves the operator on this page reading why rather than staring at a login
   *  form for a mailbox whose password never arrived.
   *
   *  `window.open` is called from the click handler's own turn for the same reason it always is: a
   *  popup opened after an await is attributed to no gesture and is blocked. So the fetch is awaited
   *  and the open happens on the result — accepted, because the alternative is a blocked tab.
   *
   *  It cannot log the operator IN. Nothing on this origin can: mailcow's session is a cookie on
   *  its own host, and a cross-origin form cannot be prefilled. What this removes is the part that
   *  was actually impossible — knowing a password no human was ever shown.
   */
  const handleWebmail = async (account: ProviderAccount) => {
    setRevealing(account.id)
    setRevealError(prev => {
      const next = { ...prev }
      delete next[account.id]
      return next
    })
    try {
      const res = await fetch(`${API}/${account.id}/webmail-credentials`, {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (!res.ok) {
        // The server distinguishes 'no password stored' from 'will not decrypt', and those
        // send an operator to different places — so its own detail is surfaced verbatim.
        const body = await res.json().catch(() => null)
        throw new Error(body?.detail ?? `Could not read the sign-in (HTTP ${res.status})`)
      }
      const creds = (await res.json()) as {
        address: string
        password: string
        webmail_url: string | null
      }
      setRevealed(prev => ({
        ...prev,
        [account.id]: { address: creds.address, password: creds.password },
      }))
      const url = creds.webmail_url ?? account.webmail_url
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      // The server's own reason, not a generic failure: "no password stored" and "will not
      // decrypt" send an operator to different places, and the endpoint distinguishes them.
      setRevealError(prev => ({
        ...prev,
        [account.id]: e instanceof Error ? e.message : String(e),
      }))
    } finally {
      setRevealing(null)
    }
  }

  const handleTest = async (account: ProviderAccount) => {
    setError(null)
    setNotice(null)
    setTesting(account.id)
    try {
      const res = await fetch(`${API}/${account.id}/test`, { method: 'POST' })
      if (!res.ok) {
        setError(await readError(res, 'The check could not be run'))
        return
      }
      const body = (await res.json()) as { account: ProviderAccount; result: CheckResult }
      setAccounts(list => list.map(a => (a.id === body.account.id ? body.account : a)))
    } finally {
      setTesting(null)
    }
  }

  /** Declare a number in the CHOSEN roles, and check that they landed.
   *
   *  `roles` is the request's own field and `role` is sent alongside it for an API that predates
   *  per-role declaration — but neither is trusted: the response is read back and any role that
   *  did not land is NAMED. HTTP 200 was exactly the answer the operator got while his number went
   *  in inbound-only, so success here means "the roles asked for exist", not "the call returned".
   */
  const declareDid = async (account: ProviderAccount, did: string, roles: string[]) => {
    const wanted = did.trim()
    if (!wanted || roles.length === 0) return false
    setError(null)
    setNotice(null)
    const res = await fetch(`${API}/${account.id}/dids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ did: wanted, roles, role: roles[0] }),
    })
    if (!res.ok) {
      setError(await readError(res, 'Could not declare the DID'))
      return false
    }
    let declared: string[] | null = null
    try {
      declared = declaredRolesFromResponse(await res.json())
    } catch {
      // An empty or unparseable body states no roles. That is "not stated", not "none declared",
      // so nothing is claimed either way and the reload below shows what actually exists.
      declared = null
    }
    const shortfall = declarationShortfallMessage(wanted, roles, declared)
    if (shortfall) setError(shortfall)
    load()
    return true
  }

  const handleAddDid = async (account: ProviderAccount) => {
    const did = (didDraft[account.id] ?? '').trim()
    if (!did) return
    const roles = didRolesFor(account.id)
    if (roles.length === 0) return
    const ok = await declareDid(account, did, roles)
    if (!ok) return
    setDidDraft(d => ({ ...d, [account.id]: '' }))
    // The role choice resets to the default — every role — rather than persisting the last one.
    // A sticky narrowed selection is how the next number quietly becomes single-role too.
    setDidRoleDraft(d => {
      const next = { ...d }
      delete next[account.id]
      return next
    })
  }

  /** ADD A ROLE TO A NUMBER ALREADY DECLARED — the repair for an inbound-only declaration.
   *
   *  Same endpoint, same request: a declaration is per (account, number, role), so adding the
   *  outbound role to an existing inbound number is a create and never an edit. Nothing is deleted,
   *  which matters because the number is declared precisely because a job is using it.
   */
  const handleAddDidRole = async (account: ProviderAccount, display: string, role: string) => {
    await declareDid(account, display, [role])
  }

  const handleRemoveDid = async (
    account: ProviderAccount,
    group: DidNumberGroup,
    didId: string,
  ) => {
    const intent = undeclareIntent(group, didId)
    // No intent means the row is not in this group — describing a removal that would not happen is
    // worse than doing nothing, so this asks for no confirmation and issues no request.
    if (!intent) return
    if (!confirm(intent.confirm)) return
    setError(null)
    const res = await fetch(`${API}/${account.id}/dids/${didId}`, { method: 'DELETE' })
    if (!res.ok) setError(await readError(res, 'Could not undeclare the DID'))
    load()
  }

  const handleDelete = async (account: ProviderAccount) => {
    setError(null)
    setNotice(null)
    if (account.referenced_by.length > 0) {
      // Refused server-side anyway; saying so here means the operator is not surprised by a 409
      // and is pointed at the action they actually want.
      setError(
        `“${account.display_name}” is referenced by ${account.referenced_by.length} job(s): ` +
          `${account.referenced_by.map(r => r.job_name).join(', ')}. Disable it instead — that ` +
          `stops new sends while leaving its history readable.`,
      )
      return
    }
    if (!confirm(`Delete “${account.display_name}”? Its credentials are gone.`)) return
    const res = await fetch(`${API}/${account.id}`, { method: 'DELETE' })
    if (!res.ok) setError(await readError(res, 'Could not delete the account'))
    load()
  }

  if (!isAdmin) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Provider Accounts</h2>
        <AdminOnly what="provider account management" />
      </div>
    )
  }

  const kindTabs = (
    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
      {kinds.map(k => (
        <Link
          key={k.kind}
          to={`/settings/provider-accounts/${k.kind}`}
          style={{
            ...smallButton,
            textDecoration: 'none',
            color: k.kind === kindParam ? theme.onPrimary : theme.text,
            background: k.kind === kindParam ? theme.primary : theme.bgAlt,
            border: `1px solid ${k.kind === kindParam ? theme.primary : '#555'}`,
          }}
        >
          {k.label}
        </Link>
      ))}
    </div>
  )

  // No kind in the URL: choose one rather than guessing. Defaulting to a kind would make the
  // page's contents depend on the registry's sort order, so an operator's bookmark would show a
  // different provider's credentials after a provider was added.
  if (!kindParam) {
    return (
      <div style={{ maxWidth: 860 }}>
        <h2 style={{ margin: '0 0 0.35rem' }}>Provider Accounts</h2>
        <p style={{ margin: '0 0 1rem', color: theme.textMuted, fontSize: '0.85rem', lineHeight: 1.6, maxWidth: 640 }}>
          Credentials that are shared by several jobs live here rather than on each job, so they
          cannot drift between them. Pick an account kind. The list comes from the provider
          registry, so a new provider appears here without a frontend change.
        </p>
        {kindsError && <div style={errorBox}>{kindsError}</div>}
        {kindTabs}
      </div>
    )
  }

  if (loading) return <div>Loading…</div>

  const fields = descriptor?.fields ?? []

  // U4 — THE SECTIONS ARE DERIVED FROM THE SERVER'S OWN SPLIT, never listed here.
  //
  // `credential_groups` and `shared_keys` come from the same two functions the server's
  // completeness check calls, so the form cannot demand a different set from the one that will be
  // enforced. A kind with fewer than two groups produces ONE unsectioned block whose fields keep
  // their descriptor `required` — byte-identical behaviour for `efax_api`, `sfax` and `srfax_api`,
  // which is what stops this change reaching forms it has no business touching.
  const groups = descriptor?.credential_groups ?? {}
  const groupNames = Object.keys(groups).sort()
  const grouped = groupNames.length >= 2
  const sharedKeys = descriptor?.shared_keys ?? []
  const byKey = new Map(fields.map(f => [f.key, f]))

  interface FormSection {
    id: string
    title: string | null
    gloss: string | null
    fields: FieldDescriptor[]
    /** True for a transport section: its fields are required only once it has been STARTED. */
    requiredWhenStarted: boolean
    started: boolean
    optional: boolean
  }

  let createSections: FormSection[]
  let anyGroupStarted = false
  if (!grouped) {
    createSections = [
      { id: 'all', title: null, gloss: null, fields, requiredWhenStarted: false, started: true, optional: false },
    ]
    anyGroupStarted = true
  } else {
    const claimed = new Set<string>([...sharedKeys, ...groupNames.flatMap(g => groups[g])])
    const sections: FormSection[] = [
      {
        id: 'shared',
        title: 'Mailbox credentials',
        // The gloss says out loud what the DERIVATION does, because the two boxes an operator
        // will look for are deliberately absent and a screen that omits them silently reports a
        // correct derivation as a missing field. The per-field help carries the detail.
        gloss:
          'Entered once and used by every protocol below. Required however the account is used.',
        fields: sharedKeys.map(k => byKey.get(k)).filter((f): f is FieldDescriptor => !!f),
        requiredWhenStarted: false,
        started: true,
        optional: false,
      },
    ]
    for (const name of groupNames) {
      const keys = groups[name]
      const started = groupStarted(keys, form)
      if (started) anyGroupStarted = true
      const copy = TRANSPORT_LABELS[name] ?? {
        title: `${name.toUpperCase()} connection`,
        gloss: 'Leave this section empty if this mailbox does not use this protocol.',
      }
      sections.push({
        id: name,
        title: copy.title,
        gloss: copy.gloss,
        fields: keys.map(k => byKey.get(k)).filter((f): f is FieldDescriptor => !!f),
        requiredWhenStarted: true,
        started,
        optional: !started,
      })
    }
    // Anything the split does not claim is an optional extra (a gateway domain, a TLS flag).
    // Rendered rather than dropped: a field the form silently omits is a value the operator
    // cannot set at all, which is a worse failure than an unlabelled section.
    const leftovers = fields.filter(f => !claimed.has(f.key))
    if (leftovers.length > 0) {
      sections.push({
        id: 'other',
        title: 'Additional settings',
        gloss:
          'Not required. A TLS-validation flag left alone means VALIDATE — the safe answer — so there is nothing to choose unless this mailbox needs the check relaxed.',
        fields: leftovers,
        requiredWhenStarted: false,
        started: true,
        optional: true,
      })
    }
    createSections = sections
  }
  const heading = descriptor ? `${descriptor.label} Accounts` : `Accounts — ${kindParam}`

  return (
    <div style={{ maxWidth: 860 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          marginBottom: '1rem',
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>{heading}</h2>
          <p
            style={{
              margin: '0.35rem 0 0',
              color: theme.textMuted,
              fontSize: '0.85rem',
              lineHeight: 1.6,
              maxWidth: 640,
            }}
          >
            Several jobs sharing one account is the expected shape. A job's leg SELECTS one of
            these records rather than restating its credentials.
          </p>
        </div>
        {descriptor && (
          <button onClick={() => { setForm(newAccountDefaults); setShowAdd(true) }} style={primaryButton}>
            + Add Account
          </button>
        )}
      </div>

      {kindTabs}

      {kindsError && <div style={errorBox}>{kindsError}</div>}
      {error && <div style={errorBox}>{error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      {!descriptor && !kindsError && (
        <div style={errorBox}>
          <strong>{kindParam}</strong> is not an account kind this deployment declares. The kinds
          come from the provider registry; pick one above.
        </div>
      )}

      {showAdd && descriptor && (
        <form onSubmit={handleCreate} style={card}>
          <h3 style={{ margin: '0 0 0.9rem', fontSize: '0.95rem' }}>New {descriptor.label} Account</h3>
          {/*
            D7 item 5 — NO PREFATORY PARAGRAPH. Two variants of an intro used to sit here, one of
            them stating the "at least one protocol section" rule. Both are gone because the form
            already says each of their facts AT THE POINT OF THE DECISION, which is where a rule is
            actionable rather than remembered:

              * a protocol section may be left empty — each section's own gloss says so, and an
                unstarted section is headed "— optional";
              * a section you start must be finished — its fields lose "(optional)" the moment it
                is started, which is `requiredWhenStarted`, the server's own rule;
              * none filled in at all is refused — the amber warning below the sections, which
                fires exactly when the condition is true instead of warning in advance;
              * manual entry needs no provisioning — the mailcow panel says so in EVERY state it
                can be in, whether it offers controls or explains why it cannot.
          */}
          {/*
            D7 item 6 — NAMING THE THING COMES BEFORE CONFIGURING IT. This box used to sit BELOW the
            mailcow panel, so the first control on the form was an optional provisioning shortcut
            and the account's own name was reached after it. It is the record's identity and the
            only field required of every kind, so it is first.
          */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Display Name</label>
            <input
              // `display_name` shares the draft with the credential fields, so it is typed as the
              // union; it is only ever written from this text box.
              value={String(form.display_name ?? '')}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              required
              style={inputStyle}
              placeholder={`${descriptor.label} — E2E`}
            />
          </div>
          {/*
            U4/U29 — THE ADDITIONAL PATH, AND IT IS ADDITIVE IN THE LITERAL SENSE. This block sits
            above fields it does not own and there is no mode switch: nothing below is disabled,
            hidden or made conditional by it or by anything it reports. An operator who ignores it
            fills the same form they filled before. All the controls ever do is write values into
            the same draft the keyboard writes into.

            U29 — AND IT RENDERS IN EVERY STATE. It used to be gated on `mailcow?.available`, which
            made the whole feature invisible in exactly the states an operator needs explained. The
            region is now unconditional for a grouped kind: either the controls, or the sentence
            saying why not.
          */}
          {grouped && (
            <div
              style={{
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
                padding: '0.75rem',
                marginBottom: '1rem',
                background: theme.bgElevated,
              }}
            >
              {mailcow?.available ? (
                <>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                Optional: let {mailcow.domain} supply the mailbox
              </div>
              <p
                style={{
                  margin: '0 0 0.6rem',
                  fontSize: '0.75rem',
                  color: theme.textMuted,
                  lineHeight: 1.6,
                }}
              >
                Create a mailbox for a DID, or pick one the domain already has — either way the
                connection settings below are filled in for you and remain editable. The mailbox is
                proven with a real IMAP login before anything is filled, which is the same evidence
                the Test button reports. You never have to use this: typing every setting by hand
                works exactly as before.
                {mailcow.limits && (
                  <>
                    {' '}
                    {mailcow.limits.mboxes_used} of {mailcow.limits.max_mailboxes} mailboxes are
                    used on this domain.
                  </>
                )}
                {mailcow.password_policy && (
                  <>
                    {' '}
                    Generated passwords satisfy this instance's policy (minimum{' '}
                    {mailcow.password_policy.min_length} characters
                    {mailcow.password_policy.requires_special
                      ? ', special character required'
                      : ', no special character required'}
                    ).
                  </>
                )}
              </p>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={labelStyle}>Create for DID</label>
                  <input
                    value={mailcowDid}
                    onChange={e => setMailcowDid(e.target.value)}
                    style={{ ...inputStyle, width: '12rem' }}
                    placeholder="13105551212"
                  />
                </div>
                <button
                  type="button"
                  disabled={mailcowBusy || !mailcowDid.trim()}
                  onClick={() => handleProvision(mailcowDid, 'create')}
                  style={secondaryButton}
                >
                  {mailcowBusy ? 'Working…' : 'Create mailbox'}
                </button>
              </div>

              {mailcow.mailboxes.length > 0 && (
                <div style={{ marginTop: '0.6rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                      <label style={labelStyle}>Or select an existing mailbox</label>
                      <select
                        value={mailcowPick}
                        onChange={e => setMailcowPick(e.target.value)}
                        style={{ ...inputStyle, width: '22rem' }}
                        data-testid="mailcow-existing-mailbox"
                      >
                        <option value="">— choose —</option>
                        {mailcow.mailboxes.map(box => (
                          /*
                            U29/U30 — A MAILBOX ANOTHER ACCOUNT ALREADY HOLDS IS MARKED, IN ITALIC,
                            AND NAMES THE HOLDER. `lower(mail_address)` is unique across every
                            account kind, so picking one of these leads to a save the database
                            refuses — and a refusal naming an index, after the form is filled, is
                            not an answer an operator can act on. Italic alone would say "different"
                            without saying how, so the holder's own display name is in the label.
                          */
                          <option
                            key={box.address}
                            value={box.local_part}
                            style={box.configured_by ? { fontStyle: 'italic' } : undefined}
                          >
                            {box.address}
                            {box.quota_mib === null ? '' : ` (${box.quota_mib} MiB)`}
                            {box.active ? '' : ' — inactive'}
                            {box.configured_by
                              ? ` — already configured in this app as “${box.configured_by.display_name}” (${box.configured_by.kind})`
                              : ''}
                          </option>
                        ))}
                      </select>
                      {/* Repeated outside the <option>, because a browser renders neither the
                          italic nor the suffix reliably once an option is SELECTED and collapsed
                          into the closed select — so the marking would vanish at the exact moment
                          the operator is about to act on it. */}
                      {(() => {
                        const picked = mailcow.mailboxes.find(b => b.local_part === mailcowPick)
                        return picked?.configured_by ? (
                          <p
                            data-testid="mailcow-already-configured"
                            style={{
                              margin: '0.3rem 0 0',
                              fontSize: '0.72rem',
                              fontStyle: 'italic',
                              color: theme.textMuted,
                              maxWidth: '30rem',
                              lineHeight: 1.5,
                            }}
                          >
                            {picked.address} is already configured in this application as “
                            {picked.configured_by.display_name}” ({picked.configured_by.kind}). A
                            mailbox may only be held by one account, so saving a second one for it
                            will be refused — edit that account instead.
                          </p>
                        ) : null
                      })()}
                    </div>
                  </div>

                  {/*
                    U30 — SELECTING IS A READ, AND THE OPERATOR CHOOSES BETWEEN SUPPLYING THE
                    PASSWORD AND DELIBERATELY RESETTING IT.

                    This block is the operator's bug report rendered as a control. Selecting an
                    existing mailbox used to reuse the create path, which rotated the mailbox's
                    password on the live mail server so that the IMAP login could succeed — from a
                    button that said "Use this mailbox". Nothing asked, nothing warned, and the
                    breakage landed on mail clients this application cannot see.

                    The default is the non-destructive branch. The reset branch is opt-in, and the
                    warning is rendered ABOVE the checkbox that arms it and above the button that
                    performs it — before the action, never as a report afterwards.
                  */}
                  <div
                    style={{
                      marginTop: '0.6rem',
                      paddingTop: '0.6rem',
                      borderTop: `1px solid ${SECTION_RULE}`,
                    }}
                  >
                    <div
                      style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}
                    >
                      This mailbox already exists, so this application does not know its password
                    </div>
                    <p
                      style={{
                        margin: '0 0 0.5rem',
                        fontSize: '0.75rem',
                        color: theme.textMuted,
                        lineHeight: 1.6,
                        maxWidth: '46rem',
                      }}
                    >
                      Selecting a mailbox changes nothing on {mailcow.domain}: it is a read, and the
                      only proof taken is an IMAP login with the password you supply. Supply the
                      current password below — or, if it is genuinely unavailable, reset it, which
                      is a change to the mail server itself.
                    </p>

                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.78rem',
                        marginBottom: '0.3rem',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="mailcow-existing-credential"
                        checked={!mailcowReset}
                        onChange={() => setMailcowReset(false)}
                        style={{ marginRight: '0.4rem' }}
                      />
                      I have this mailbox&rsquo;s current password
                    </label>
                    {!mailcowReset && (
                      <div style={{ margin: '0 0 0.5rem 1.4rem' }}>
                        <label style={labelStyle}>Current mailbox password</label>
                        <input
                          type="password"
                          value={mailcowExistingPassword}
                          onChange={e => setMailcowExistingPassword(e.target.value)}
                          style={{ ...inputStyle, width: '18rem' }}
                          autoComplete="new-password"
                          data-testid="mailcow-existing-password"
                        />
                      </div>
                    )}

                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.78rem',
                        marginBottom: '0.3rem',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="mailcow-existing-credential"
                        checked={mailcowReset}
                        onChange={() => setMailcowReset(true)}
                        style={{ marginRight: '0.4rem' }}
                        data-testid="mailcow-reset-choice"
                      />
                      Reset this mailbox&rsquo;s password to a new one
                    </label>
                    {/* THE WARNING, BEFORE THE ACTION. Rendered as soon as the reset branch is
                        chosen and above the button that performs it, in the server's own words so
                        this page cannot soften them. */}
                    {mailcowReset && (
                      <div
                        data-testid="mailcow-reset-warning"
                        style={{
                          ...errorBox,
                          margin: '0 0 0.5rem 1.4rem',
                          fontSize: '0.75rem',
                          lineHeight: 1.6,
                          maxWidth: '46rem',
                        }}
                      >
                        <strong>This changes the mail server.</strong>{' '}
                        {mailcow.password_reset_warning}
                      </div>
                    )}

                    <button
                      type="button"
                      disabled={
                        mailcowBusy ||
                        !mailcowPick ||
                        (!mailcowReset && !mailcowExistingPassword)
                      }
                      onClick={() =>
                        mailcowReset
                          ? handleProvision(mailcowPick, 'reset', { confirmReset: true })
                          : handleProvision(mailcowPick, 'select', {
                              existingPassword: mailcowExistingPassword,
                            })
                      }
                      style={secondaryButton}
                      data-testid="mailcow-use-existing"
                    >
                      {mailcowBusy
                        ? 'Working…'
                        : mailcowReset
                          ? 'Reset the password and use this mailbox'
                          : 'Use this mailbox'}
                    </button>
                  </div>
                </div>
              )}

              {/* Reported inside the panel, so an optional path's failure never looks like the
                  form's failure. */}
              {mailcowError && (
                <div style={{ ...errorBox, marginTop: '0.6rem', marginBottom: 0 }}>
                  {mailcowError} The settings below can still be typed in by hand.
                </div>
              )}
              {mailcowNote && (
                <div style={{ ...noticeBox, marginTop: '0.6rem', marginBottom: 0 }}>
                  {mailcowNote}
                </div>
              )}
                </>
              ) : (
                /*
                  U29 — THE SENTENCE THAT USED TO BE AN EMPTY SPACE. One of these renders in every
                  state where the controls are not offered, so the reader always learns which of
                  "switched off", "incompletely configured", "key will not decrypt" and "did not
                  answer" they are looking at. `mailcowVerdictKey` is total, so this branch can
                  never fall through to nothing.
                */
                (() => {
                  const key = mailcowVerdictKey(mailcowProbe, mailcow) ?? 'unknown'
                  const verdict = MAILCOW_VERDICTS[key]
                  return (
                    <div data-testid={`mailcow-verdict-${key}`}>
                      <div
                        style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}
                      >
                        Mailbox provisioning: {verdict.headline}
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.75rem',
                          color: theme.textMuted,
                          lineHeight: 1.6,
                        }}
                      >
                        {verdict.body} {MAILCOW_MANUAL_UNAFFECTED}
                      </p>
                      {/* THE SERVER'S OWN WORDS, verbatim and never paraphrased: the reason names
                          the missing settings, the decrypt failure or the failing request, and a
                          summary of it is what sent the last reader looking in the wrong place. */}
                      {mailcow && (
                        <p
                          style={{
                            margin: '0.5rem 0 0',
                            fontSize: '0.72rem',
                            color: theme.textMuted,
                            fontFamily: 'ui-monospace, monospace',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          state: {mailcow.state}
                          {mailcow.domain ? ` \u2022 domain: ${mailcow.domain}` : ''}
                          {mailcow.mail_host ? ` \u2022 host: ${mailcow.mail_host}` : ''}
                          {mailcow.reason ? `\n${mailcow.reason}` : ''}
                        </p>
                      )}
                    </div>
                  )
                })()
              )}
            </div>
          )}
          {createSections.map(section => (
            <div key={section.id} style={{ marginBottom: '1rem' }}>
              {section.title && (
                <div
                  style={{
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: theme.text,
                    borderTop: `1px solid ${SECTION_RULE}`,
                    paddingTop: '0.8rem',
                    marginBottom: '0.2rem',
                  }}
                >
                  {section.title}
                  {section.optional && (
                    <span style={{ fontWeight: 400, color: theme.textMuted }}> — optional</span>
                  )}
                </div>
              )}
              {section.gloss && (
                <div style={{ ...hintStyle, marginBottom: '0.5rem' }}>{section.gloss}</div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {section.fields.map(f => {
                  // The EFFECTIVE requirement, not the descriptor's union answer. See
                  // `groupStarted`: a per-protocol field is required only once its own section
                  // has been started, which is exactly the server's rule.
                  const isRequired = section.requiredWhenStarted
                    ? section.started
                    : f.required
                  return (
                    <div key={f.key}>
                      <label style={labelStyle}>
                        {f.label}
                        {isRequired ? '' : ' (optional)'}
                      </label>
                      <FieldInput
                        field={f}
                        value={form[f.key] ?? ''}
                        onChange={val => setForm(v => ({ ...v, [f.key]: val }))}
                        required={isRequired}
                      />
                      {f.help && <div style={hintStyle}>{f.help}</div>}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
          {grouped && !anyGroupStarted && (
            <div style={{ ...hintStyle, color: '#ffa726' }}>
              No protocol section is filled in yet. A record no leg can read is not a saved
              account — it is one that will fail at send time claiming to be fine, so the server
              refuses it too.
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="submit" disabled={busy} style={primaryButton}>
              {busy ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAdd(false)
                setForm(newAccountDefaults)
              }}
              style={secondaryButton}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {descriptor && accounts.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: theme.textMuted }}>
          No {descriptor.label} accounts configured. A job cannot use a leg of this kind until one
          exists.
        </div>
      ) : (
        accounts.map(account => (
          <div key={account.id} style={card}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '1rem',
              }}
            >
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 600 }}>{account.display_name}</div>
                <div style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.2rem' }}>
                  <span style={{ color: account.enabled ? '#4caf50' : theme.textMuted }}>
                    {account.enabled ? '● Enabled' : '○ Disabled'}
                  </span>
                  {' · '}
                  {account.kind_label}
                  {' · '}updated {fmtDate(account.updated_at)}
                  {account.updated_by ? ` by ${account.updated_by}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => void handleTest(account)}
                  disabled={testing === account.id}
                  style={smallButton}
                >
                  {testing === account.id ? 'Testing…' : 'Test'}
                </button>
                <button onClick={() => handleToggle(account)} style={smallButton}>
                  {account.enabled ? 'Disable' : 'Enable'}
                </button>
                {/*
                  WEBMAIL — only for a `mail` account, and only when the server supplied a URL.
                  `webmail_url` comes from `MAILCOW_API_BASE` server-side and is absent when mailcow
                  is not configured, so there is nothing to render and nothing to guess. It is not
                  derived from `imap_host` even where that holds the same value: webmail is a service
                  of the mailcow deployment, while `imap_host` is a mail-protocol endpoint an
                  operator may point elsewhere.
                */}
                {account.webmail_url && (
                  <button
                    onClick={() => void handleWebmail(account)}
                    disabled={revealing === account.id}
                    style={smallButton}
                    title="Open webmail and reveal this mailbox's sign-in"
                  >
                    {revealing === account.id ? 'Opening…' : 'Webmail'}
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditing(editing === account.id ? null : account.id)
                    setEditForm({})
                  }}
                  style={smallButton}
                >
                  {editing === account.id ? 'Close' : 'Edit'}
                </button>
                <button onClick={() => handleDelete(account)} style={dangerButton}>
                  Delete
                </button>
              </div>
            </div>

            {revealError[account.id] && (
              <div
                style={{
                  marginTop: '0.6rem',
                  fontSize: '0.78rem',
                  color: theme.primary,
                  lineHeight: 1.6,
                }}
              >
                {revealError[account.id]}
              </div>
            )}

            {revealed[account.id] && (
              <div
                style={{
                  marginTop: '0.6rem',
                  padding: '0.6rem 0.7rem',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  display: 'grid',
                  gap: '0.4rem',
                }}
              >
                {/*
                  SHOWN BECAUSE IT WAS ASKED FOR, AND SAYING SO. This is the only place in the
                  application that displays a stored secret, and the operator opened webmail in
                  another tab expecting to type it. Hiding it behind a second reveal would make the
                  button useless for the case it exists for: a mailbox mailcow provisioned, whose
                  password no human has ever seen.
                */}
                <CopyRow label="Username" value={revealed[account.id].address} />
                <CopyRow label="Password" value={revealed[account.id].password} />
                <div style={{ fontSize: '0.72rem', color: theme.textMuted, lineHeight: 1.6 }}>
                  Paste these into the webmail tab that just opened. This disclosure is recorded in
                  the audit log against your account. Closing this panel forgets it here — it is not
                  cached, and the stored copy is unchanged.
                  <button
                    onClick={() =>
                      setRevealed(prev => {
                        const next = { ...prev }
                        delete next[account.id]
                        return next
                      })
                    }
                    style={{ ...smallButton, marginLeft: '0.5rem' }}
                  >
                    Hide
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: '0.7rem' }}>
              <CheckVerdict result={account.verified_detail} verifiedAt={account.verified_at} />
            </div>

            {account.polls_outbound_status && (
              // B31 — THE EFFECTIVE CADENCE, ON THE CARD, without opening the editor. What is in
              // force is what matters to a reader of a measurement, and it is not the same fact
              // as what this account has stored: an account that set nothing still has a cadence,
              // inherited, and it still bounds every rung-3 t_fax_sent this account produces.
              <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: theme.textMuted }}>
                Status poll:{' '}
                <strong style={{ color: theme.text }}>
                  {account.effective_poll_interval_seconds
                    ? `every ${account.effective_poll_interval_seconds}s`
                    : 'not reported'}
                </strong>{' '}
                ({pollIntervalSourceLabel(account.poll_interval_source)})
                {account.effective_poll_backoff_max_seconds
                  ? `, backing off to at most ${account.effective_poll_backoff_max_seconds}s on a rate-limit refusal`
                  : ''}
              </div>
            )}

            <div style={{ marginTop: '0.9rem', display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              {fields.map(f => {
                const stored = account.presence[f.presence_flag] === true
                // A DISPLAYABLE field shows its VALUE here, not the word "stored" (U9). "stored"
                // is the right summary for a secret — there is nothing legible to print — but for
                // a username or an endpoint it withholds the one fact the operator came to check:
                // WHICH user, WHICH mailbox, WHICH regional host. `stored` still decides the
                // colour, and a value that is present but undecryptable falls back to the word,
                // because `presence` is computed from the raw column while `values` needs the key.
                const shown = f.displayable ? account.values?.[f.key] : undefined
                const legible =
                  shown !== undefined && shown !== null && shown !== '' ? String(shown) : null
                return (
                  <div key={f.key} style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                    {f.label}:{' '}
                    <strong style={{ color: stored ? theme.text : f.required ? '#ef5350' : theme.textMuted }}>
                      {legible ?? (stored ? 'stored' : 'not set')}
                    </strong>
                  </div>
                )
              })}
            </div>

            {/*
              T5.42 — server-generated, and a kind with no callback URLs now renders NO SECTION.

              D7 item 4 removed a third branch that ASSERTED the absence of any account-level
              callback URL in prose. It is deliberately not reworded: that sentence SHIPPED FALSE
              (the next comment records how), on a screen that was already being handed the URL it
              denied. A sentence with that history does not get a third attempt, and an absent
              section already tells an operator there is nothing here to register — without
              asserting anything that can go stale when a leg moves again.

              `tests/test_did_validity_and_account_webhook_surface.py` pins BOTH halves: the
              wording must not return, and this gate must stay, or a kind with no URL renders a
              rule with nothing under it.
            */}
            {Boolean(descriptor?.has_account_callbacks || account.webhook_url) && (
              <div
                style={{
                  marginTop: '0.9rem',
                  paddingTop: '0.75rem',
                  borderTop: `1px solid ${SECTION_RULE}`,
                }}
              >
                {descriptor?.has_account_callbacks ? (
                  <>
                    <div style={{ fontSize: '0.8rem', marginBottom: '0.4rem' }}>
                      Callback URLs — paste these into the provider's account-level callback
                      settings
                    </div>
                    <div style={{ display: 'grid', gap: '0.35rem' }}>
                      <CallbackUrl label="Inbound" url={account.inbound_callback_url} />
                      <CallbackUrl label="Outbound" url={account.outbound_callback_url} />
                    </div>
                  </>
                ) : (
                  <>
                    {/*
                      THIS BRANCH DID NOT EXIST AND THE ACCOUNT OWNED THE URL ANYWAY. Item 4 moved
                      the eFax inbound webhook onto the `efax_api` account, and the API has been
                      returning `webhook_url` on the account ever since — nothing rendered it. So
                      the page told the operator this kind had no account-level callback URL at
                      all, which was false, on a screen that was already being handed the URL. That
                      is the defect this project keeps hitting: a working engine and a surface
                      describing a different one.

                      It is a separate branch from `has_account_callbacks` because the two are not
                      the same fact. SFax exposes a PAIR of callbacks derived from its account id;
                      eFax has ONE inbound target whose path IS `webhook_id` — the value already
                      typed into eFax's portal by hand, which is why it is displayed and never
                      regenerated.
                    */}
                    <div style={{ fontSize: '0.8rem', marginBottom: '0.4rem' }}>
                      Inbound webhook URL — register this in eFax&rsquo;s portal for this
                      eAPI App ID
                    </div>
                    <div style={{ display: 'grid', gap: '0.35rem' }}>
                      <CallbackUrl label="Inbound" url={account.webhook_url ?? null} />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Declared DIDs — what T5.41 turns into the job editor's destination select. */}
            <div
              style={{
                marginTop: '0.9rem',
                paddingTop: '0.75rem',
                borderTop: `1px solid ${SECTION_RULE}`,
              }}
            >
              <div style={{ fontSize: '0.8rem', marginBottom: '0.4rem' }}>
                Declared DIDs{' '}
                <span style={{ color: theme.textMuted, fontSize: '0.74rem' }}>
                  — the numbers this account owns. A job's destination is chosen from them.
                </span>
              </div>
              {/*
                THE SRFAX PER-DID LIMIT, STATED WHERE THE DECISION IS MADE (T6.20 / G10.13).
                It belongs here rather than in the docs because declaring a second DID on an
                SRFax account is the moment the capability is given up, and nothing later in the
                UI can show it: Get_Fax_Inbox reports the SENDER's CallerID and no to-number, so
                a received fax cannot be attributed to the DID it arrived on. The admission
                filter then has nothing to test and rests on the account, with selection falling
                to the run token alone. This is a hard SRFax product limit, not a gap in our
                code, and it applies ONLY to the API/polling leg — an SRFax IMAP leg is
                unaffected, because our mailbox naming convention carries the DID.
              */}
              {account.account_kind === 'srfax_api' && account.dids.length > 1 && (
                <div
                  style={{
                    fontSize: '0.74rem',
                    color: theme.textMuted,
                    marginBottom: '0.45rem',
                    lineHeight: 1.45,
                  }}
                >
                  <strong>SRFax cannot report which DID a fax arrived on.</strong> With more than
                  one DID declared on a polling account, per-DID admission is given up: inbound
                  matching rests on the account plus the run token. This affects the API/polling
                  leg only — an SRFax IMAP leg is unaffected, because the mailbox carries the DID.
                  To keep per-DID attribution, use one sub-user per DID, or receive that DID over
                  IMAP.
                </div>
              )}
              {account.dids.length === 0 ? (
                <div style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                  None declared. A job on this account will fall back to a typed destination
                  number, so nothing is blocked — declaring them only removes the typing.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {/* ONE ENTRY PER NUMBER, listing the roles it holds — not one row per
                      declaration. A DID declared inbound AND outbound is two rows by design (that
                      is the supported round trip: send from and receive at the same number), and
                      rendering a row each made one number look like two numbers — the same
                      misdescription in a new place. Grouping on `did_normalised` is what makes
                      them the same number: `7148449145` and `+17148449145` normalise identically,
                      so they were never two DIDs. */}
                  {groupDidsByNumber(account.dids).map(group => {
                    const held = rolesHeld(group)
                    const addable = missingRoles(group, didRoles)
                    return (
                      <div
                        key={group.normalised}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '0.4rem',
                          fontSize: '0.78rem',
                          background: theme.bg,
                          border: `1px solid ${theme.border}`,
                          borderRadius: 3,
                          padding: '0.25rem 0.45rem',
                        }}
                      >
                        <code>{group.display}</code>
                        <span style={{ color: theme.textMuted }}>
                          {held.map(r => roleLabel(r)).join(' + ')}
                        </span>
                        {/* THE IN-PLACE REPAIR for a number declared in one role only. Without it
                            the sole route is undeclare-and-re-declare, which is not a route the
                            operator has: the number is declared because a job uses it, and the
                            declaration is what admits deliveries in the meantime. */}
                        {addable.map(role => (
                          <button
                            key={role}
                            type="button"
                            onClick={() => void handleAddDidRole(account, group.display, role)}
                            style={{ ...smallButton, padding: '0 0.35rem' }}
                            title={
                              roleGloss(role) ??
                              `Also declare ${group.display} for ${roleLabel(role).toLowerCase()}`
                            }
                          >
                            + {roleLabel(role).toLowerCase()}
                          </button>
                        ))}
                        {/* One button per ROLE, each SAYING WHAT IT REMOVES. Undeclaring is per
                            row, so removing "inbound" must not silently drop the outbound
                            declaration of the same number — and where a row is the number's last,
                            the button says the number goes. */}
                        {group.entries.map(e => {
                          const intent = undeclareIntent(group, e.id)
                          if (!intent) return null
                          return (
                            <button
                              key={e.id}
                              type="button"
                              onClick={() => void handleRemoveDid(account, group, e.id)}
                              style={{ ...smallButton, padding: '0 0.35rem' }}
                              aria-label={`${intent.label} — ${group.display}`}
                              title={intent.confirm}
                            >
                              × {intent.label}
                            </button>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )}
              {/* DECLARING A NUMBER: the roles come with BOTH ALREADY CHOSEN.
                  This default is the fix, not a convenience. The API defaulted a declaration to
                  `inbound` alone, so a number declared here sat on screen looking usable while
                  every send from it was refused for having no outbound DID. The operator's model —
                  "a number I own sends and receives" — was right, and the form silently disagreed
                  with it. The roles offered are the registry's `did_roles`, so this control needs
                  no edit when the vocabulary changes and carries no per-provider branch: a role
                  means the same thing on eFax, SFax and SRFax. */}
              <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
                <input
                  value={didDraft[account.id] ?? ''}
                  onChange={e => setDidDraft(d => ({ ...d, [account.id]: e.target.value }))}
                  placeholder="+15551234567"
                  style={{ ...inputStyle, maxWidth: 200 }}
                />
                {didRoles.map(role => {
                  const checked = didRolesFor(account.id).includes(role)
                  return (
                    <label
                      key={role}
                      style={{ fontSize: '0.76rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                      title={roleGloss(role) ?? undefined}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setDidRoleDraft(d => ({
                            ...d,
                            [account.id]: toggleDidRole(didRolesFor(account.id), role, didRoles),
                          }))
                        }
                      />
                      {roleLabel(role)}
                    </label>
                  )
                })}
                <button
                  type="button"
                  onClick={() => void handleAddDid(account)}
                  disabled={
                    !(didDraft[account.id] ?? '').trim() || didRolesFor(account.id).length === 0
                  }
                  style={smallButton}
                >
                  Declare DID
                </button>
              </div>
              {didRolesFor(account.id).length === 0 && (
                <div style={{ fontSize: '0.74rem', color: '#ffa726', marginTop: '0.3rem' }}>
                  Choose at least one role — a declaration with no role would permit neither leg.
                </div>
              )}
              {didRolesFor(account.id).length === 1 && (
                <div style={{ fontSize: '0.74rem', color: theme.textMuted, marginTop: '0.3rem' }}>
                  This will declare the number for{' '}
                  {roleLabel(didRolesFor(account.id)[0]).toLowerCase()} ONLY. A leg needing the
                  other role will be refused even though the number is listed above; you can add
                  the missing role later without undeclaring this one.
                </div>
              )}
            </div>

            <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: theme.textMuted }}>
              {account.referenced_by.length === 0 ? (
                'Not referenced by any job.'
              ) : (
                <>
                  Used by:{' '}
                  {account.referenced_by.map((r, i) => (
                    <span key={r.job_id}>
                      {i > 0 && ', '}
                      <strong style={{ color: theme.text }}>{r.job_name}</strong> (
                      {r.legs.join(' + ')}
                      {r.deleted ? ', deleted' : ''})
                    </span>
                  ))}
                  {'. '}Deleting is refused while any job references it; disable it instead.
                </>
              )}
            </div>

            {editing === account.id && (
              <div
                style={{
                  marginTop: '0.9rem',
                  paddingTop: '0.75rem',
                  borderTop: `1px solid ${SECTION_RULE}`,
                }}
              >
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: theme.textMuted }}>
                  Leave a field <strong>empty to keep</strong> what is stored. Secrets are never
                  returned, so there is nothing to display — only to replace.
                </p>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={labelStyle}>Display Name</label>
                  <input
                    value={String(editForm.display_name ?? '')}
                    onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))}
                    style={inputStyle}
                    placeholder={account.display_name}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  {fields.map(f => {
                    const stored = account.presence[f.presence_flag] === true
                    // A NON-SECRET VALUE IS SHOWN, not masked. Every field used to get the
                    // bullet placeholder, so `api_base_url` read as "stored — leave blank to
                    // keep" and an operator could not see WHICH regional endpoint the account
                    // pointed at. After the account migration two accounts pointed at the wrong
                    // region, every credential test failed with "401 Bad credentials", and the
                    // one field that explained it was the one field the form refused to show.
                    // Secrets stay write-only; a value that is not a secret must be legible or
                    // the screen that owns it cannot diagnose it.
                    //
                    // U9 — THE TEST IS `displayable`, NOT `secret`, and the difference is the
                    // whole of U9. A `*_enc` username is a SECRET by storage (encrypted at rest,
                    // sent write-only on save) and DISPLAYABLE by declaration, so the response
                    // returns its decrypted current value. Keying on `secret` here showed every
                    // username as "•••••••• (stored — leave blank to keep)" on the only screen
                    // that could correct a typo in it, which is what the operator reported twice.
                    const plainValue = f.displayable ? (account.values?.[f.key] ?? '') : undefined
                    return (
                      <div key={f.key}>
                        <label style={labelStyle}>{f.label}</label>
                        <FieldInput
                          field={f}
                          value={editForm[f.key] ?? plainValue ?? ''}
                          onChange={val => setEditForm(v => ({ ...v, [f.key]: val }))}
                          // U4 — NOTHING IS REQUIRED ON EDIT, because on this form an empty box
                          // means KEEP WHAT IS STORED. The shared `FieldInput` applied the
                          // descriptor's `required` here too, so the browser refused to submit
                          // unless every required box was non-empty — which contradicts the
                          // sentence directly above it and made changing one port mean retyping
                          // the password. The server's own validator already treats a stored
                          // value as satisfying its requirement on update
                          // (`_missing_required`), so this is the form catching up to the rule,
                          // not a relaxation of it.
                          required={false}
                          placeholder={
                            f.secret && !f.displayable
                              ? stored
                                ? '•••••••• (stored — leave blank to keep)'
                                : 'not set'
                              : 'not set'
                          }
                        />
                      </div>
                    )
                  })}
                </div>
                {(descriptor?.polls_outbound_status ?? account.polls_outbound_status) && (
                  <div
                    style={{
                      marginTop: '0.9rem',
                      paddingTop: '0.75rem',
                      borderTop: `1px dashed ${SECTION_RULE}`,
                    }}
                  >
                    <label style={labelStyle}>Outbound status poll cadence</label>
                    <div style={{ ...hintStyle, marginBottom: '0.6rem' }}>
                      Empty uses the provider default; clearing a value reverts to it.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label style={labelStyle}>Poll interval (seconds)</label>
                        <input
                          type="number"
                          min={descriptor?.poll_interval_min_seconds ?? 15}
                          max={descriptor?.poll_interval_max_seconds ?? 3600}
                          value={String(
                            editForm[POLL_INTERVAL_KEY] ?? account.poll_interval_seconds ?? '',
                          )}
                          onChange={e =>
                            setEditForm(f => ({ ...f, [POLL_INTERVAL_KEY]: e.target.value }))
                          }
                          style={inputStyle}
                          placeholder={
                            descriptor?.poll_interval_default_seconds
                              ? `${descriptor.poll_interval_default_seconds} (provider default)`
                              : 'default'
                          }
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Backoff ceiling (seconds)</label>
                        <input
                          type="number"
                          min={descriptor?.poll_interval_min_seconds ?? 15}
                          max={descriptor?.poll_backoff_max_seconds_ceiling ?? 21600}
                          value={String(
                            editForm[POLL_BACKOFF_KEY] ?? account.poll_backoff_max_seconds ?? '',
                          )}
                          onChange={e =>
                            setEditForm(f => ({ ...f, [POLL_BACKOFF_KEY]: e.target.value }))
                          }
                          style={inputStyle}
                          placeholder="default"
                        />
                      </div>
                    </div>
                    <p
                      style={{ margin: '0.55rem 0 0', fontSize: '0.73rem', color: theme.textMuted }}
                    >
                      The ceiling is as far as a <strong>rate-limit refusal</strong> may widen the
                      interval — and only a refusal does. A timeout or a reset connection says
                      nothing about our cadence, so it never backs off; treating one as a refusal
                      would let a single network blip inflate every following measurement on this
                      account.
                    </p>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem' }}>
                  <button
                    onClick={() => void handleSaveEdit(account.id)}
                    disabled={busy}
                    style={primaryButton}
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(null)
                      setEditForm({})
                    }}
                    style={secondaryButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  )
}

/**
 * D7 item 7 — THE SECTION RULE FOR THIS PAGE'S ACCOUNT CARDS, and deliberately NOT `theme.border`.
 *
 * One account card stacks six of these sections on a single surface — the credential summary, the
 * callback URLs, the declared DIDs, the credential-check panel, the edit form and the poll cadence
 * inside it. At `theme.border` (#333 on a #1e1e1e card) the rule between them is very nearly
 * invisible, so the sections read as one long column and the boundary an operator is looking for is
 * not where their eye stops.
 *
 * `theme.border` IS NOT RAISED TO FIX THAT, and the restraint is the point: every table, card,
 * input and header in the application draws its lines from that one value, so editing it would
 * restyle surfaces nobody asked to have touched. Stacking six sections on one card is a property of
 * THIS page, so the treatment lives here.
 *
 * A brighter line at the same 1px weight rather than a thicker one — the sections need to be told
 * apart, not boxed, and a 2px rule on a card this dense reads as six separate cards.
 */
const SECTION_RULE = '#585858'

const card: React.CSSProperties = {
  background: theme.bgAlt,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: '1.1rem',
  marginBottom: '1rem',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.78rem',
  color: theme.textMuted,
  marginBottom: '0.2rem',
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: theme.textMuted,
  marginTop: '0.2rem',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  background: theme.bg,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  fontSize: '0.9rem',
  boxSizing: 'border-box',
}

const primaryButton: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: theme.primary,
  color: theme.onPrimary,
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
}

const secondaryButton: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: `1px solid #555`,
  background: theme.bgAlt,
  color: theme.text,
  borderRadius: 4,
  cursor: 'pointer',
}

const smallButton: React.CSSProperties = {
  padding: '0.25rem 0.55rem',
  border: `1px solid #555`,
  background: theme.bgAlt,
  color: theme.text,
  borderRadius: 3,
  cursor: 'pointer',
  fontSize: '0.78rem',
}

const dangerButton: React.CSSProperties = {
  ...smallButton,
  border: '1px solid #ef5350',
  color: '#ef5350',
}

const errorBox: React.CSSProperties = {
  background: '#3a1e1e',
  border: '1px solid #5a2a2a',
  color: '#ef9a9a',
  padding: '0.75rem',
  borderRadius: 4,
  marginBottom: '1rem',
  fontSize: '0.85rem',
  lineHeight: 1.6,
}

const noticeBox: React.CSSProperties = {
  background: '#1e3a24',
  border: '1px solid #2a5a33',
  color: '#a5d6a7',
  padding: '0.75rem',
  borderRadius: 4,
  marginBottom: '1rem',
  fontSize: '0.85rem',
}
